import type { AudioElementValue, VideoElementValue } from '@vedalib/editor/lib/elements'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageSource, FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'
import { chainName } from 'utils'
import { FileTypeEnum, getParent } from 'utils'

import FloatingPanel from 'components/Editor/FloatingPanel'
import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import { PLAYBACK_RATE_OPTIONS } from 'components/MediaPlayer/constants'
import { getDurationWithBreakpoints } from 'components/MediaPlayer/helper'
import type { IFieldConfig } from 'components/formKit'
import {
  FieldColorPicker,
  FieldFile,
  FieldSegmented,
  FieldSelect,
  FieldSlider,
  FieldTimeRange,
  FormField,
} from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import type { IFormKitControlProps } from 'components/formKit/types'
import { TileButton } from 'components/uiKit/Button'
import { KitSize } from 'components/uiKit/KitTypes'
import { isMixed } from 'components/uiKit/KitUtils'
import { Typography } from 'components/uiKit/Typography'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import { setElement, updateBlock } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getActiveElementId, getBlockId, getElementState } from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { getFileByUsage } from 'utils/files'

import type { MediaFormValueType } from '../mediaTypes'
import type { MediaEditorElement } from '../mediaTypes'
import TimeLine from './TimeLine'

const useFileType = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<MediaEditorElement>(name, 2)

  return useMemo(
    () => ({
      props: {
        fileType: value?.type as FileTypeEnum.AUDIO | FileTypeEnum.VIDEO,
        nullable: true,
        preview: true,
        showSource: true,
      },
    }),
    [value?.type],
  )
}

const useShowVideo = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<MediaEditorElement>(name, 2)
  return useMemo(() => ({ hidden: value?.type !== FileTypeEnum.VIDEO }), [value?.type])
}

const usePercentsInfo = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<MediaEditorElement>(name, 2)

  return useMemo(
    () => ({
      info:
        value?.type === 'video'
          ? t('elements.media.form.videoPercentHint')
          : t('elements.media.form.audioPercentHint'),
    }),
    [value.type],
  )
}

const useResetFieldsAfterFileChange = (): Partial<
  IFieldConfig<Block, React.ComponentProps<typeof FieldFile<FileUsage>>>
> => {
  const block = useFormData<Block>()

  return useMemo(
    () => ({
      effect: (changeData) => {
        const { value: file, name } = changeData
        const { parent, parentName } = getParent<AudioElementValue | VideoElementValue>(name, block)

        return {
          ...changeData,
          name: parentName,
          value: {
            ...CONFIG_ELEMENTS.audio.defaultValue,
            file,
            coverImage: parent?.coverImage,
            controls: parent?.controls,
            repeat: parent?.repeat,
            volume: parent?.volume,
            playbackRate: parent?.playbackRate,
            percent: parent?.percent,
            autoPlay: parent?.autoPlay,
            disableSeeking: parent?.disableSeeking,
            displayPlayback: parent?.displayPlayback,
          },
        }
      },
    }),
    [block],
  )
}

const FILE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'file',
  useConfigs: [useFileType, useResetFieldsAfterFileChange],
})

const COVER_IMAGE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'coverImage',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasAccessibility: true,
  },
})

const AUTO_PLAY = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'autoPlay',
  label: t('elements.media.form.autoPlay'),
})

const REPEAT = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'repeat',
  label: t('elements.media.form.repeat'),
})

const DISABLE_SEEKING = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'disableSeeking',
  label: t('elements.media.form.disableSeeking'),
  info: t('elements.media.form.disableSeekingInfo'),
})

const DISPLAY_PLAYBACK = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'displayPlayback',
  label: t('elements.media.form.displayPlayback'),
})

const CONTROLS = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'controls',
  label: t('elements.media.form.controls'),
  info: t('input.tooltip.mediaPlayerControls'),
  useConfigs: [useShowVideo],
})

const VOLUME = genElementValueField({
  as: FieldSlider,
  name: 'volume',
  label: t('elements.media.form.volume'),
  props: { showInput: true, min: 0, max: 100 },
})

const PLAYBACK_RATE = genElementValueField({
  as: FieldSelect<number>,
  name: 'playbackRate',
  label: t('elements.media.form.playbackRate'),
  info: t('elements.media.form.playbackRate.info'),
  size: KitSize.S,
  props: { options: PLAYBACK_RATE_OPTIONS, fluid: true },
})

const useBreakpointsProps = ({
  name,
}: IFieldConfig<Block>): Partial<
  IFieldConfig<Block, IFormKitControlProps<[number, number] | null>>
> => {
  const value = useFormData<AudioElementValue | VideoElementValue>(name, 3)
  const elementId = useProjectContext(getActiveElementId) || ''
  const state = useProjectContext(getElementState, elementId)
  const duration = state?.duration as number
  const originalDuration = state?.originalDuration as number

  return useMemo(
    () => ({
      hidden: value?.file?.source === FileUsageSource.url,
      disabled: !duration,
      props: {
        maxTime: originalDuration,
        format: duration && duration > 3600 ? 'HH:mm:ss' : 'mm:ss',
        placeholder: duration && duration > 3600 ? '00:00:00' : '00:00',
      },
    }),
    [duration, value?.file?.source, originalDuration],
  )
}

const useBreakpointsUpdateDuration = (
  _params: IFieldConfig<Block>,
): Partial<IFieldConfig<Block, IFormKitControlProps<[number, number] | null>>> => {
  const dispatch = useProjectDispatch()
  const elementId = useProjectContext(getActiveElementId) || ''
  const state = useProjectContext(getElementState, elementId)
  const originalDuration = state?.originalDuration as number

  return useMemo(
    () => ({
      effect: (changeData) => {
        const { value: breakpoints, name } = changeData
        if (breakpoints && originalDuration) {
          const newDuration = getDurationWithBreakpoints(
            originalDuration,
            !isMixed(breakpoints) ? breakpoints : undefined,
          )
          dispatch(setElement({ id: elementId, value: { ...state, duration: newDuration } }))
        }
        return { name, value: breakpoints }
      },
    }),
    [dispatch, elementId, state, originalDuration],
  )
}

const BREAKPOINTS = genElementValueField({
  as: FieldTimeRange,
  name: 'file.params.breakpoints',
  label: t('elements.media.form.slice'),
  useConfigs: [useBreakpointsUpdateDuration, useBreakpointsProps],
})

const PERCENT = genElementValueField({
  as: FieldSlider,
  name: 'percent',
  label: t('input.label.percent'),
  props: { max: 100, min: 0, showInput: true },
  useConfigs: [usePercentsInfo],
})

const CAPTIONS = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'captions',
  props: {
    fileType: FileTypeEnum.CAPTIONS,
    nullable: true,
    showSource: true,
  },
})

const ENABLE_CC_BY_DEFAULT = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'enableCCByDefault',
  label: t('elements.media.form.enableCCByDefault'),
  defaultValue: false,
})

const OVERLAY = genElementValueField({
  as: FieldColorPicker<string | null>,
  name: 'overlay',
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions],
})

const ValueForm: MediaFormValueType = ({ name, element: { value, type }, state }) => {
  const coverFile = getFileByUsage(value.coverImage, FileUsageTypeEnum.image)
  const hasCaptions = Boolean(value.captions?.id)
  const hasTimePoints = Boolean(value.timePoints?.length)
  const dispatch = useProjectDispatch()
  const blockId = useProjectContext(getBlockId)

  const onChange = useCallback(
    (value: unknown) =>
      blockId && dispatch(updateBlock({ id: blockId, name: `${name}.timePoints`, value })),
    [blockId, dispatch, name],
  )

  return (
    <Padding bottom gap sides>
      <FormField config={chainName(name, FILE)} />
      <FormField config={chainName(name, CONTROLS)} />
      <FormField config={chainName(name, AUTO_PLAY)} />
      <FormField config={chainName(name, REPEAT)} />
      <FormField config={chainName(name, DISABLE_SEEKING)} />
      <FormField config={chainName(name, DISPLAY_PLAYBACK)} />
      <FormField config={chainName(name, VOLUME)} />
      <FormField config={chainName(name, PLAYBACK_RATE)} />
      <FormField config={chainName(name, BREAKPOINTS)} />
      <FormField config={chainName(name, PERCENT)} />
      {type === FileTypeEnum.VIDEO && (
        <FloatingPanel
          header={<Typography weight='medium'>{t('elements.media.form.cover')}</Typography>}
          panel={
            <Padding gap>
              <FormField config={chainName(name, COVER_IMAGE)} />
              <FormField config={chainName(name, OVERLAY)} />
            </Padding>
          }
        >
          {({ open }) => (
            <TileButton active={open} icon='otherAdd' name='coverSettings' styleType='stroke'>
              <PanelIconImage file={value.coverImage} icon='otherBlock' />
              <Typography rows={1} styleType='hint' tooltip={coverFile?.name}>
                {coverFile?.name || t('elements.media.form.cover')}
              </Typography>
            </TileButton>
          )}
        </FloatingPanel>
      )}
      <FloatingPanel
        header={<Typography weight='medium'>{t('elements.media.form.captions')}</Typography>}
        panel={
          <Padding gap>
            <Typography styleType='hint'>{t('elements.media.form.ccHint')}</Typography>
            <FormField config={chainName(name, CAPTIONS)} />
            <FormField config={chainName(name, ENABLE_CC_BY_DEFAULT)} />
          </Padding>
        }
      >
        {({ open }) => (
          <TileButton active={open} icon='otherAdd' name='captionsSettings' styleType='stroke'>
            <PanelIconImage hasValue={hasCaptions} icon='energy' iconSize={20} />
            <Typography rows={1} styleType='hint'>
              {t('elements.media.form.captions')}
            </Typography>
          </TileButton>
        )}
      </FloatingPanel>
      <FloatingPanel
        header={<Typography weight='medium'>{t('elements.media.form.timecode')}</Typography>}
        panel={
          <Padding gap>
            {/* NEED REFACTOR TIMELINE TO NEW FORM_FIELD AND DELETE onChange here*/}
            <TimeLine
              duration={state?.duration || 0}
              onChange={onChange}
              timePoints={value.timePoints}
              type={type}
            />
          </Padding>
        }
      >
        {({ open }) => (
          <TileButton active={open} icon='otherAdd' name='timecodeSettings' styleType='stroke'>
            <PanelIconImage hasValue={hasTimePoints} icon='energy' iconSize={20} />
            <Typography rows={1} styleType='hint'>
              {t('elements.media.form.timecode')}
            </Typography>
          </TileButton>
        )}
      </FloatingPanel>
    </Padding>
  )
}

export default ValueForm
